/**
 * Scope registry for the contextual LP chatbot widget.
 *
 * Every scope ships with:
 * - `starterPromptsKey` — next-intl key resolving to an array of prompt strings
 * - `inlineChipLabelKey` — (optional) next-intl key for the inline-chip label
 * - `dify` — the Dify chatflow id (currently always 'lp-contextual')
 *
 * New scopes are added here AND in messages/*.json under the same keys.
 * The medical/regulatory team reviews via PR diff.
 */

import type { RootMessageKey } from "@/lib/intl-types"

/**
 * All inline-chip label keys, literal-typed so `t(scopeConfig.inlineChipLabelKey)`
 * picks next-intl's single-argument overload (none of these messages carry
 * placeholders). Add new scopes here as well as in `SCOPES` below.
 */
type InlineChipKey =
  | "chat.scopes.biomarkers.chip"
  | "chat.scopes.diseases.chip"
  | "chat.scopes.fullbodymri.chip"
  | "chat.scopes.insurance-ch.chip"
  | "chat.scopes.pricing.chip"
  | "chat.scopes.tests.chip"

export interface ChatScopeConfig {
  /** Full dotted path into the message tree, including the `chat.` prefix. */
  starterPromptsKey: RootMessageKey
  inlineChipLabelKey?: InlineChipKey
  dify: "lp-contextual"
}

export const SCOPES = {
  general: {
    starterPromptsKey: "chat.scopes.general.prompts",
    dify: "lp-contextual",
  },
  fullbodymri: {
    starterPromptsKey: "chat.scopes.fullbodymri.prompts",
    inlineChipLabelKey: "chat.scopes.fullbodymri.chip",
    dify: "lp-contextual",
  },
  "insurance-ch": {
    starterPromptsKey: "chat.scopes.insurance-ch.prompts",
    inlineChipLabelKey: "chat.scopes.insurance-ch.chip",
    dify: "lp-contextual",
  },
  pricing: {
    starterPromptsKey: "chat.scopes.pricing.prompts",
    inlineChipLabelKey: "chat.scopes.pricing.chip",
    dify: "lp-contextual",
  },
  // Biomarker detail + listing pages. One scope serves both — the specific
  // marker reaches the bot via page_url / page_title, not the scope value.
  biomarkers: {
    starterPromptsKey: "chat.scopes.biomarkers.prompts",
    inlineChipLabelKey: "chat.scopes.biomarkers.chip",
    dify: "lp-contextual",
  },
  // Disease (what-we-detect) detail + listing pages. Same one-scope rationale.
  diseases: {
    starterPromptsKey: "chat.scopes.diseases.prompts",
    inlineChipLabelKey: "chat.scopes.diseases.chip",
    dify: "lp-contextual",
  },
  // Generic scope for individual test pages without bespoke prompts. The
  // specific test reaches Dify via page_url / page_title, not the scope value.
  // Per-test bespoke prompts are keyed on the page slug via `promptScope`
  // (see promptScopeForPath in lib/scope-for-path.ts).
  tests: {
    starterPromptsKey: "chat.scopes.tests.prompts",
    inlineChipLabelKey: "chat.scopes.tests.chip",
    dify: "lp-contextual",
  },
  // Blood check-up ad landing pages (/go/bloodtest*). Prompts-only (like
  // `general`) — the inline-chip label isn't rendered anywhere today.
  bloodtest: {
    starterPromptsKey: "chat.scopes.bloodtest.prompts",
    dify: "lp-contextual",
  },
  // Membership page + /go/membership.
  membership: {
    starterPromptsKey: "chat.scopes.membership.prompts",
    dify: "lp-contextual",
  },
  // Sample-report page (/report).
  report: {
    starterPromptsKey: "chat.scopes.report.prompts",
    dify: "lp-contextual",
  },
  // How-it-works page — process-focused prompts.
  "how-it-works": {
    starterPromptsKey: "chat.scopes.how-it-works.prompts",
    dify: "lp-contextual",
  },
} as const satisfies Record<string, ChatScopeConfig>

export type ScopeKey = keyof typeof SCOPES

/**
 * Defensive fallback for any scope not in the registry — resolves to the same
 * generic `tests` config so a stray scope still renders a working chip rather
 * than throwing. Test pages should pass the registered `tests` scope (not a raw
 * slug) so `usePromptVariant` reads existing message keys.
 */
const TESTS_FALLBACK: ChatScopeConfig = SCOPES.tests

export function getScope(key: string): ChatScopeConfig {
  return (SCOPES as Record<string, ChatScopeConfig>)[key] ?? TESTS_FALLBACK
}
